import {
  captureExceptionWithOptions,
  SentryTags,
} from '@porterin/component-library/src/ts-utils/analytics/sentry/Sentry';
import axios, { AxiosError } from 'axios';
import { getLocaleHeaders } from '@porterin/component-library/src/utils/LocaleHelper';
import type { Contexts } from '@sentry/types';

const getParamsPayload = (url: string) => {
  try {
    const urlObj = new URL(url);
    const searchParams = new URLSearchParams(urlObj.searchParams);
    return Object.fromEntries(searchParams.entries());
  } catch {
    return {};
  }
};

const getApiUrlInfo = (url?: string) => {
  if (!url) return { host: 'N.A', pathname: 'N.A' };
  try {
    const urlObj = new URL(url);
    return { host: urlObj.host, pathname: urlObj.pathname };
  } catch {
    return { host: 'N.A', pathname: 'N.A' };
  }
};

export const fireApiErrorSentry = (
  error: any,
  source: string,
  extra_tags?: SentryTags | ((err: AxiosError) => SentryTags),
  extra_contexts?: Contexts,
) => {
  if (axios.isAxiosError(error)) {
    const axiosError = error as AxiosError;
    const localeHeaders = getLocaleHeaders();
    const urlInfo = getApiUrlInfo(axiosError?.config?.url);
    const tags = {
      'ApiError.is_axios_error': true,
      'ApiError.source': source,
      'ApiError.code': axiosError?.code || 'N.A',
      'ApiError.api_host': urlInfo.host,
      'ApiError.api_endpoint': urlInfo.pathname,
      'ApiError.status': axiosError.response?.status || 'N.A',
      'LocaleInfo.language': localeHeaders?.['preferred-language'] ?? 'N.A',
      'LocaleInfo.country': localeHeaders?.country ?? 'N.A',
      ...(typeof extra_tags === 'function'
        ? extra_tags(axiosError)
        : extra_tags),
    };
    const contexts = {
      Axios: {
        request_headers: axiosError?.config?.headers?.toJSON() || 'N.A',
        request_params: getParamsPayload(axiosError?.config?.url || ''),
        request_data: axiosError?.config?.data || 'N.A',
        response_data: axiosError.response?.data || 'N.A',
      },
      ...extra_contexts,
    };

    captureExceptionWithOptions(
      `Error in ${source}: ${axiosError.message}`,
      tags,
      contexts,
    );
  } else {
    captureExceptionWithOptions(
      `Error in ${source}: ${error?.message || String(error)}`,
      {
        'ApiError.source': source,
        'ApiError.is_axios_error': false,
      },
    );
  }
  return;
};
